import { z } from "zod";
import { IdType } from "./base.validator";
import { paginateSchema } from "./pagination.validator";

export enum TimeAwayTypes {
  SickLeave = "Sick Leave",
  VacationLeave = "Vacation Leave",
  CarersLeave = "Carer's Leave",
}

export const timeAwayCredit = z.object({
  timeAwayTypeId: IdType,
  timeAwayType: z.nativeEnum(TimeAwayTypes),
  total: z.number(),
});

export type TimeAwayCredit = typeof timeAwayCredit;

export const totalUserTimeAwayCredits = z.object({
  userId: IdType,
  totals: timeAwayCredit.array(),
});

export type TotaluserTimeAwayCredits = z.infer<typeof totalUserTimeAwayCredits>;

export const checkListItem = z.object({
  id: z.string(),
  question: z.string(),
});
export const checkListAnswer = z.object({
  id: IdType,
  checkListItemId: IdType,
  answer: z.boolean().nullable(),
});

export const approverPerson = z.object({
  id: IdType,
  firstName: z.string(),
  lastName: z.string(),
});
export type ApproverPerson = z.infer<typeof approverPerson>;

export const personApprover = z.object({
  approverType: z.enum(["DEPARTMENT_HEAD", "SPECIFIC_PERSON"]),
  user: approverPerson.nullable(),
});
export const roleApprover = z.object({
  approverType: z.literal("SPECIFIC_ROLE"),
  role: z
    .object({
      id: IdType,
      title: z.string(),
    })
    .nullable(),
});

export const approverDetails = z.union([personApprover, roleApprover]);
export type ApproverDetails = z.infer<typeof approverDetails>;

export const approvalHistoryItem = z.object({
  id: IdType,
  message: z.string().nullable(),
  isApproved: z.boolean().nullable(),
  approverDetails,
  approverUser: approverPerson.nullable(),
  answers: checkListAnswer.array(),
});
export type ApprovalHistoryItem = z.infer<typeof approvalHistoryItem>;

export const createdTimeAway = z.object({
  id: IdType,
  userId: IdType,
  hours: z.coerce.number().array(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  timeAwayTypeId: IdType,
  isApproved: z.boolean().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string().datetime(),
  approvalMessage: z.string().nullable(),
  isUserRequest: z.boolean(),
  isSubtract: z.boolean().default(true),
  approvalHistory: approvalHistoryItem.array(),
  checklist: checkListItem.array(),
  currentApproverIndex: z.number(),
  finalApproverUser: z
    .object({
      id: IdType,
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullable(),
});

export type CreatedTimeAway = z.infer<typeof createdTimeAway>;
export const paginatedCreatedProfile = paginateSchema(createdTimeAway);

export type PaginatedCreatedTimeAwaysDto = z.infer<
  typeof paginatedCreatedProfile
>;

export const findTimeAwayFilter = createdTimeAway
  .pick({
    id: true,
    userId: true,
    startDate: true,
    endDate: true,
    timeAwayTypeId: true,
  })
  .extend({
    isApproved: z.enum(["true", "false", "null"]).transform((v) => {
      switch (v) {
        case "true":
          return true;
        case "false":
          return false;
        case "null":
          return null;
      }
    }),
    isUserRequest: z.enum(["true", "false"]).transform((v) => {
      switch (v) {
        case "true":
          return true;
        case "false":
          return false;
      }
    }),
  })
  .partial();

export const createdTimeAwayTypeList = z.object({
  data: createdTimeAway.array(),
});

export const timeAwayHistory = createdTimeAway
  .omit({ isSubtract: true, timeAwayTypeId: true })
  .extend({
    hours: z.coerce.number(),
    timeAwayType: z.nativeEnum(TimeAwayTypes),
  });

export type TimeAwayHistory = z.infer<typeof timeAwayHistory>;
export const paginatedTimeAwayHistoryDto = paginateSchema(timeAwayHistory);

export type PaginatedTimeAwayHistoryDto = z.infer<
  typeof paginatedTimeAwayHistoryDto
>;

export type CreatedTimeAwayList = z.infer<typeof createdTimeAwayTypeList>;

export const createLeave = z
  .object({
    // userId: IdType,
    hours: z.coerce.number().min(1).array(),
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
    timeAwayTypeId: IdType,
    reason: z.string().nullable(),
  })
  .describe("The representation of the leave that exists in the database");

export type CreateLeaveDto = z.infer<typeof createLeave>;

export const createdHistory = z.object({
  id: IdType,
  userId: z.string(),
  timeAwayType: z.nativeEnum(TimeAwayTypes),
  change: z.number().positive().or(z.number().negative()),
  remarks: z.string(),
  createdAt: z.string().datetime(),
});

export type CreatedHistory = z.infer<typeof createdHistory>;

export const createdHistoryList = z.object({
  data: createdTimeAway.array(),
});

export type CreatedHistoryList = z.infer<typeof createdHistoryList>;

export const deleteLeave = z.object({
  id: IdType,
  userId: z.string().nullable(),
  timeAwayTypeId: IdType,
});

export const queryLeaveHistory = z
  .object({
    page: z.number(),
    limit: z.number(),
  })
  .partial();

export type QueryLeaveHistory = z.infer<typeof queryLeaveHistory>;

export const createTimeAwayAdjustmentInput = createdTimeAway
  .pick({
    hours: true,
    reason: true,
    userId: true,
    timeAwayTypeId: true,
  })
  .extend({
    hours: z.coerce.number().refine((arg) => arg !== 0, "Must not be zero"),
  });
export type CreateTimeAwayAdjustmentInput = z.infer<
  typeof createTimeAwayAdjustmentInput
>;

export const checkListAnswerInput = z.object({
  id: IdType,
  answer: z.boolean(),
});
export type CheckListAnswerInput = z.infer<typeof checkListAnswerInput>;

export const finalizeTimeAwayInput = z.object({
  isApproved: z.coerce.boolean(),
  approvalMessage: z.string().optional(),
  checkListAnswers: checkListAnswerInput.array().optional(),
  override: z.boolean().optional(),
});

export type FinalizeTimeAwayInput = z.infer<typeof finalizeTimeAwayInput>;
